import React, { useEffect, useRef, useState, useCallback } from 'react';
import { previewUrl, downloadUrl, getWindowInfo } from '../api/dicomApi';
import ViewportFrame from './ViewportFrame';

const ZOOM_MIN = 1;
const ZOOM_MAX = 5;
const ZOOM_STEP = 0.25;

export default function PreviewModal({ file, allFiles, onSelect, onClose }) {
  const [winInfo, setWinInfo] = useState(null);
  const [wc, setWc] = useState(null);
  const [ww, setWw] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [inverted, setInverted] = useState(false);

  const [currentFrame, setCurrentFrame] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [fps, setFps] = useState(10);

  const [measureMode, setMeasureMode] = useState(null); // null | 'distance' | 'angle'
  const [measurements, setMeasurements] = useState([]);
  const [pendingPoints, setPendingPoints] = useState([]);
  const [naturalSize, setNaturalSize] = useState({ w: 1, h: 1 });

  const dragRef = useRef(null);
  const svgRef = useRef(null);
  const playIntervalRef = useRef(null);

  const resetView = () => {
    setZoom(1); setPan({ x: 0, y: 0 });
    setRotation(0); setFlipH(false); setFlipV(false); setInverted(false);
    setWc(null); setWw(null);
  };

  useEffect(() => {
    if (!file) return;
    resetView();
    setCurrentFrame(0);
    setPlaying(false);
    setMeasureMode(null);
    setMeasurements([]);
    setPendingPoints([]);
    getWindowInfo(file.id)
      .then(({ data }) => setWinInfo(data))
      .catch(() => setWinInfo({ supported: false }));
  }, [file]);

  // Cine playback loop
  useEffect(() => {
    if (playIntervalRef.current) clearInterval(playIntervalRef.current);
    const frameCount = winInfo?.frameCount || 1;
    if (playing && frameCount > 1) {
      playIntervalRef.current = setInterval(() => {
        setCurrentFrame((f) => (f + 1) % frameCount);
      }, 1000 / fps);
    }
    return () => { if (playIntervalRef.current) clearInterval(playIntervalRef.current); };
  }, [playing, fps, winInfo]);

  const goToOffset = useCallback((offset) => {
    if (!allFiles || !file) return;
    const idx = allFiles.findIndex((f) => f.id === file.id);
    if (idx === -1) return;
    const next = allFiles[idx + offset];
    if (next) onSelect(next);
  }, [allFiles, file, onSelect]);

  useEffect(() => {
    const onKey = (e) => {
      if (!file) return;
      if (e.key === 'ArrowRight') goToOffset(1);
      if (e.key === 'ArrowLeft') goToOffset(-1);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [file, goToOffset, onClose]);

  if (!file) return null;

  const frameCount = winInfo?.frameCount || 1;
  const params = {};
  if (winInfo?.supported !== false && wc !== null && ww !== null) { params.wc = wc; params.ww = ww; }
  if (frameCount > 1) params.frame = currentFrame;

  const startDrag = (e) => {
    if (measureMode) return;
    if (zoom <= 1) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
  };
  const onDrag = (e) => {
    if (!dragRef.current) return;
    setPan({ x: dragRef.current.panX + (e.clientX - dragRef.current.startX), y: dragRef.current.panY + (e.clientY - dragRef.current.startY) });
  };
  const endDrag = () => { dragRef.current = null; };

  const imgTransform = `rotate(${rotation}deg) scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1})`;
  const wrapperTransform = `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`;

  const pixelSpacing = winInfo?.pixelSpacing; // { rowMm, colMm } or null

  const toImagePoint = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX; pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const local = pt.matrixTransform(ctm.inverse());
    return { x: local.x, y: local.y };
  };

  const distancePx = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
  const distanceLabel = (a, b) => {
    if (pixelSpacing) {
      const dxMm = (b.x - a.x) * pixelSpacing.colMm;
      const dyMm = (b.y - a.y) * pixelSpacing.rowMm;
      return `${Math.hypot(dxMm, dyMm).toFixed(1)} mm`;
    }
    return `${distancePx(a, b).toFixed(0)} px`;
  };
  const angleLabel = (a, b, c) => {
    const v1 = { x: a.x - b.x, y: a.y - b.y };
    const v2 = { x: c.x - b.x, y: c.y - b.y };
    const dot = v1.x * v2.x + v1.y * v2.y;
    const mag = Math.hypot(v1.x, v1.y) * Math.hypot(v2.x, v2.y);
    const deg = (Math.acos(Math.min(1, Math.max(-1, dot / mag))) * 180) / Math.PI;
    return `${deg.toFixed(1)}°`;
  };

  const onSvgClick = (e) => {
    if (!measureMode) return;
    const pt = toImagePoint(e.clientX, e.clientY);
    if (!pt) return;
    const need = measureMode === 'distance' ? 2 : 3;
    const next = [...pendingPoints, pt];
    if (next.length < need) {
      setPendingPoints(next);
      return;
    }
    const id = Date.now();
    const label = measureMode === 'distance' ? distanceLabel(next[0], next[1]) : angleLabel(next[0], next[1], next[2]);
    setMeasurements((m) => [...m, { id, type: measureMode, points: next, label }]);
    setPendingPoints([]);
  };

  const clearMeasurements = () => { setMeasurements([]); setPendingPoints([]); };
  const viewLocked = zoom !== 1 || pan.x !== 0 || pan.y !== 0 || rotation !== 0 || flipH || flipV;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-panel border border-line rounded-sm max-w-5xl w-full max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-line">
          <h3 className="text-text font-medium font-data text-sm">{file.original_filename}</h3>
          <button onClick={onClose} className="text-textdim hover:text-text transition-colors">✕</button>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <ViewportFrame
              className="bg-black flex items-center justify-center min-h-[340px] overflow-hidden select-none"
              label={`${zoom.toFixed(2)}x`}
            >
              <div
                style={{ cursor: zoom > 1 && !measureMode ? (dragRef.current ? 'grabbing' : 'grab') : measureMode ? 'crosshair' : 'default' }}
                className="w-full h-full min-h-[340px] flex items-center justify-center"
                onMouseDown={startDrag}
                onMouseMove={onDrag}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
              >
                <div style={{ transform: wrapperTransform, position: 'relative' }}>
                  <img
                    src={previewUrl(file.id, params)}
                    alt="DICOM preview"
                    draggable={false}
                    className="max-w-full max-h-[440px] object-contain transition-transform duration-75 block"
                    style={{ transform: imgTransform, filter: inverted ? 'invert(1)' : 'none' }}
                    onLoad={(e) => setNaturalSize({ w: e.target.naturalWidth || 1, h: e.target.naturalHeight || 1 })}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const el = e.target.parentElement?.nextElementSibling;
                      if (el) el.style.display = 'block';
                    }}
                  />
                  <svg
                    ref={svgRef}
                    viewBox={`0 0 ${naturalSize.w} ${naturalSize.h}`}
                    className="absolute inset-0 w-full h-full"
                    style={{ transform: imgTransform, cursor: measureMode ? 'crosshair' : 'default', pointerEvents: measureMode ? 'auto' : 'none' }}
                    onClick={onSvgClick}
                  >
                    {measurements.map((m) => (
                      <g key={m.id} stroke="#4DD9C4" strokeWidth={Math.max(1, naturalSize.w / 300)} fill="#4DD9C4">
                        {m.points.slice(0, -1).map((p, i) => (
                          <line key={i} x1={p.x} y1={p.y} x2={m.points[i + 1].x} y2={m.points[i + 1].y} />
                        ))}
                        {m.points.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={Math.max(2, naturalSize.w / 200)} />)}
                        <text x={m.points[Math.floor(m.points.length / 2)].x + 6} y={m.points[Math.floor(m.points.length / 2)].y - 6} fontSize={Math.max(10, naturalSize.w / 30)} stroke="none" fill="#4DD9C4">
                          {m.label}
                        </text>
                      </g>
                    ))}
                    {pendingPoints.map((p, i) => (
                      <circle key={i} cx={p.x} cy={p.y} r={Math.max(2, naturalSize.w / 200)} fill="#E8A83C" />
                    ))}
                  </svg>
                </div>

                <div style={{ display: 'none' }} className="text-sm text-textdim text-center max-w-xs px-4">
                  Preview unavailable for this file. It may use an unsupported compressed transfer syntax, or the decoder may have failed to read this specific file's pixel data — check the backend terminal for a "[preview]" log line with the details. Metadata editing, anonymize, and download still work normally.
                </div>
              </div>
            </ViewportFrame>

            {/* Series navigation + thumbnail strip */}
            {allFiles && allFiles.length > 1 && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1">
                  <button onClick={() => goToOffset(-1)} className="text-xs px-2 py-1 rounded-sm bg-raised hover:bg-line text-textdim hover:text-text transition-colors">← Prev</button>
                  <span className="text-xs text-textdim font-data">{allFiles.findIndex((f) => f.id === file.id) + 1} / {allFiles.length}</span>
                  <button onClick={() => goToOffset(1)} className="text-xs px-2 py-1 rounded-sm bg-raised hover:bg-line text-textdim hover:text-text transition-colors">Next →</button>
                </div>
                <div className="flex gap-1 overflow-x-auto pb-1">
                  {allFiles.map((f) => (
                    <img
                      key={f.id}
                      src={previewUrl(f.id)}
                      alt=""
                      onClick={() => onSelect(f)}
                      className={`h-12 w-12 object-cover rounded-sm cursor-pointer border-2 flex-shrink-0 ${f.id === file.id ? 'border-phosphor' : 'border-transparent opacity-60 hover:opacity-100'}`}
                      onError={(e) => { e.target.style.opacity = 0.2; }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Cine playback (multi-frame) */}
            {frameCount > 1 && (
              <div className="mt-3 flex items-center gap-2 bg-raised rounded-sm px-3 py-2">
                <button onClick={() => setPlaying((p) => !p)} className="text-xs px-2 py-1 rounded-sm bg-phosphor hover:bg-phosphordim text-ink font-semibold transition-colors">
                  {playing ? 'Pause' : 'Play'}
                </button>
                <input
                  type="range" min={0} max={frameCount - 1} value={currentFrame}
                  onChange={(e) => { setPlaying(false); setCurrentFrame(Number(e.target.value)); }}
                  className="flex-1"
                />
                <span className="text-xs text-textdim font-data w-16 text-right">{currentFrame + 1}/{frameCount}</span>
                <label className="text-xs text-textdim flex items-center gap-1">
                  fps
                  <input type="number" min={1} max={30} value={fps} onChange={(e) => setFps(Number(e.target.value) || 1)} className="w-12 bg-panel border border-line rounded-sm px-1 font-data focus:outline-none focus:border-phosphor" />
                </label>
              </div>
            )}

            {/* Rotate / flip / invert */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button onClick={() => setRotation((r) => (r + 90) % 360)} className="text-xs px-3 py-1.5 rounded-sm bg-raised hover:bg-line text-textdim hover:text-text transition-colors">Rotate 90°</button>
              <button onClick={() => setFlipH((v) => !v)} className={`text-xs px-3 py-1.5 rounded-sm transition-colors ${flipH ? 'bg-phosphordim text-ink' : 'bg-raised hover:bg-line text-textdim hover:text-text'}`}>Flip H</button>
              <button onClick={() => setFlipV((v) => !v)} className={`text-xs px-3 py-1.5 rounded-sm transition-colors ${flipV ? 'bg-phosphordim text-ink' : 'bg-raised hover:bg-line text-textdim hover:text-text'}`}>Flip V</button>
              <button onClick={() => setInverted((v) => !v)} className={`text-xs px-3 py-1.5 rounded-sm transition-colors ${inverted ? 'bg-phosphordim text-ink' : 'bg-raised hover:bg-line text-textdim hover:text-text'}`}>Invert</button>
              <button onClick={resetView} className="text-xs px-3 py-1.5 rounded-sm bg-raised hover:bg-line text-textdim hover:text-text transition-colors">Reset view</button>
            </div>

            {/* Measurement tools */}
            <div className="mt-3">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => { setMeasureMode((m) => (m === 'distance' ? null : 'distance')); setPendingPoints([]); }}
                  className={`text-xs px-3 py-1.5 rounded-sm transition-colors ${measureMode === 'distance' ? 'bg-phosphordim text-ink' : 'bg-raised hover:bg-line text-textdim hover:text-text'}`}
                >
                  Distance
                </button>
                <button
                  onClick={() => { setMeasureMode((m) => (m === 'angle' ? null : 'angle')); setPendingPoints([]); }}
                  className={`text-xs px-3 py-1.5 rounded-sm transition-colors ${measureMode === 'angle' ? 'bg-phosphordim text-ink' : 'bg-raised hover:bg-line text-textdim hover:text-text'}`}
                >
                  Angle
                </button>
                {measurements.length > 0 && (
                  <button onClick={clearMeasurements} className="text-xs px-3 py-1.5 rounded-sm bg-rose/90 hover:bg-rose text-ink font-medium transition-colors">
                    Clear measurements
                  </button>
                )}
              </div>
              {measureMode && (
                <p className="text-xs text-textdim mt-1">
                  Click {measureMode === 'distance' ? '2 points' : '3 points (vertex second)'} on the image.
                  {viewLocked && ' Tip: reset zoom/pan/rotation first for the most accurate placement.'}
                  {!pixelSpacing && ' No pixel spacing in this file — measurement shown in pixels, not mm.'}
                </p>
              )}
            </div>

            <div className="mt-3">
              <a href={downloadUrl(file.id)} className="inline-block px-4 py-2 rounded-sm bg-raised hover:bg-line text-text text-sm transition-colors">
                Download edited file
              </a>
            </div>
          </div>

          <dl className="text-sm text-text space-y-2">
            <Row label="Patient Name" value={file.patient_name} />
            <Row label="Patient ID" value={file.patient_id} mono />
            <Row label="Study Date" value={file.study_date} mono />
            <Row label="Institution" value={file.institution_name} />
            <Row label="Modality" value={file.modality} mono />
            <Row label="Study Description" value={file.study_description} />
            <Row label="Dimensions" value={file.rows_px && file.columns_px ? `${file.columns_px}×${file.rows_px}` : '—'} mono />
            <Row label="Frames" value={frameCount > 1 ? frameCount : '1 (single frame)'} mono />
            <Row label="SOP Instance UID" value={file.sop_instance_uid} mono />
          </dl>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-textdim text-[11px] uppercase tracking-wider pt-0.5">{label}</dt>
      <dd className={`text-right ${mono ? 'font-data text-xs' : ''}`}>{value || '—'}</dd>
    </div>
  );
}
